import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import i18n from 'i18next';
import api, { authAPI, permissionsAPI, entitlementsAPI } from '../lib/api';

const AuthContext = createContext(null);

// Keep i18n synced with the user's saved locale preference. Falls back to
// browser/localStorage detection when no preference is on file.
function applyUserLocale(user) {
  const locale = user?.preferred_locale;
  if (locale && i18n.language !== locale) {
    i18n.changeLanguage(locale).catch(() => {});
    try { localStorage.setItem('sl_locale', locale); } catch (_) {}
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [entitlements, setEntitlements] = useState({ inventory_core: true, crm: false, dispatch: false });
  const [loading, setLoading] = useState(true);

  // Keep i18n synced with whatever user object we're currently holding.
  useEffect(() => {
    applyUserLocale(user);
  }, [user?.preferred_locale]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await permissionsAPI.getMyPermissions();
      setPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions({});
    }
  }, []);

  const fetchEntitlements = useCallback(async () => {
    try {
      const response = await entitlementsAPI.get();
      setEntitlements(response.data || { inventory_core: true, crm: false, dispatch: false });
    } catch (error) {
      console.error('Failed to fetch entitlements:', error);
      // Default to inventory-only on error
      setEntitlements({ inventory_core: true, crm: false, dispatch: false });
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Validate the stored JSON is parseable
          let parsedUser;
          try {
            parsedUser = JSON.parse(storedUser);
          } catch (parseErr) {
            // Corrupted user JSON — clear everything and go to login
            console.error('Corrupt user data in localStorage, clearing auth state');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            setLoading(false);
            return;
          }
          
          setUser(parsedUser);
          
          // Verify token with backend (only if online)
          if (navigator.onLine) {
            const response = await authAPI.getMe();
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            // Fetch permissions and entitlements in parallel — must complete before loading=false
            await Promise.all([fetchPermissions(), fetchEntitlements(), api.post('/storage/session')]).catch(() => {});
          }
        } catch (error) {
          if (error?.response?.status === 401) {
            // Auth failed — the interceptor will have attempted token refresh.
            // If we reach here, both the access and refresh tokens are invalid.
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            setUser(null);
            setPermissions({});
          } else {
            // Network error, timeout, or other non-auth issue.
            // Keep the stored user for offline support, but ensure the app doesn't
            // stay in an unusable state. If the user/token pair seems invalid
            // (e.g., hard redirect already triggered by interceptor), clean up.
            const hasToken = localStorage.getItem('access_token');
            if (!hasToken) {
              // The interceptor already cleared storage (redirect to /login in progress)
              setUser(null);
              setPermissions({});
            }
          }
        }
      }
      setLoading(false);
    };

    // Safety timeout: if initAuth takes longer than 20s, force loading=false
    // to prevent an infinite spinner. This handles hung network connections.
    let timedOut = false;
    const safetyTimer = setTimeout(() => {
      timedOut = true;
      console.error('Auth initialization timed out after 20s — forcing recovery');
      // If we're stuck, clear stale auth state and let the app show login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setUser(null);
      setPermissions({});
      setLoading(false);
    }, 20000);

    initAuth().finally(() => {
      clearTimeout(safetyTimer);
      if (!timedOut) {
        setLoading(false);
      }
    });
  }, [fetchPermissions, fetchEntitlements]);

  // Refresh user + permissions + entitlements when the tab regains focus.
  // Handles the common scenario where an admin toggles a user's permissions
  // while that user is already logged in — the user would otherwise have to
  // manually reload to see the change (e.g. to get a newly-granted "POs" nav).
  useEffect(() => {
    let lastRefresh = 0;
    const REFRESH_COOLDOWN_MS = 30_000; // avoid hammering the API on every tab flick

    const refreshIfStale = async () => {
      if (!localStorage.getItem('access_token')) return;
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefresh < REFRESH_COOLDOWN_MS) return;
      lastRefresh = now;

      try {
        // Refresh the user payload first (role / flags may have changed).
        const me = await authAPI.getMe();
        if (me?.data) {
          localStorage.setItem('user', JSON.stringify(me.data));
          setUser(me.data);
        }
      } catch (e) {
        // Non-fatal — interceptor handles 401s.
      }
      // Then refresh permissions + entitlements in parallel.
      await Promise.all([fetchPermissions(), fetchEntitlements()]).catch(() => {});
    };

    window.addEventListener('visibilitychange', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);
    return () => {
      window.removeEventListener('visibilitychange', refreshIfStale);
      window.removeEventListener('focus', refreshIfStale);
    };
  }, [fetchPermissions, fetchEntitlements]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Set the file-access cookie before any page renders private images.
    await api.post('/storage/session').catch(() => {});
    setUser(userData);
    
    // Fetch permissions and entitlements in parallel — await to ensure they're ready
    await Promise.all([fetchPermissions(), fetchEntitlements()]).catch(() => {});
    
    // NOTE: Timezone auto-detection removed to prevent overriding
    // tenant's configured timezone. Users should set timezone explicitly
    // via Company Settings if needed.
    
    return userData;
  };

  const signup = async (payload) => {
    // payload: { company_name, first_name, last_name, email, password, referral_code, accept_terms, business_country, industry, industry_other_text, plan_tier }
    // i18n: send the browser language hint so the backend can seed
    // preferred_locale for the new account. Never overrides an existing user.
    const browserLocale = (typeof navigator !== 'undefined' && navigator.language) || 'en';
    const response = await authAPI.signup({ ...payload, browser_locale: browserLocale });
    const { access_token, refresh_token, user: userData, card_setup_required } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Set the file-access cookie before any page renders private images.
    await api.post('/storage/session').catch(() => {});
    setUser(userData);
    
    // Fetch permissions and entitlements in parallel, non-blocking
    Promise.all([fetchPermissions(), fetchEntitlements()]).catch(() => {});
    
    // Auto-detect and persist timezone on signup (new tenant)
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedTimezone) {
        const tzResponse = await api.post('/tenant/timezone/detect', { timezone: detectedTimezone });
        if (tzResponse.data.updated) {
          const updatedUser = { ...userData, tenant_timezone: detectedTimezone };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
        }
      }
    } catch (e) {
      console.log('Timezone auto-detect skipped:', e.message);
    }
    
    return { ...userData, card_setup_required: !!card_setup_required };
  };

  // Keep the HttpOnly file-access cookie fresh so <img>/<a> links to protected
  // tenant files (job/customer attachments, form files) authenticate.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    const refresh = () => api.post('/storage/session').catch(() => {});
    refresh();
    const timer = setInterval(refresh, 4 * 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, [userId]);

  const logout = () => {
    api.delete('/storage/session').catch(() => {});
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
    setPermissions({});
    setEntitlements({ inventory_core: true, crm: false, dispatch: false });
  };

  // Direct auth setter for MFA flows and other cases where login is already done
  const setAuth = async ({ token, refreshToken, user: userData }) => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    // Set the file-access cookie before any page renders private images.
    await api.post('/storage/session').catch(() => {});
    setUser(userData);
    
    // Fetch permissions and entitlements in parallel, non-blocking
    Promise.all([fetchPermissions(), fetchEntitlements()]).catch(() => {});
  };

  // Update tenant timezone (owner only)
  const updateTenantTimezone = async (newTimezone) => {
    const response = await api.put('/tenant/timezone', { timezone: newTimezone });
    if (response.data.timezone) {
      // Update local user data
      const updatedUser = { ...user, tenant_timezone: response.data.timezone };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
    return response.data;
  };

  // Permission check helper
  const hasPerm = useCallback((permissionKey) => {
    // Owner always has all permissions
    if (user?.role === 'owner') return true;
    return permissions[permissionKey] === true;
  }, [user?.role, permissions]);

  // Entitlement check helper
  const hasEntitlement = useCallback((module) => {
    return entitlements[module] === true;
  }, [entitlements]);

  // Addon check helper
  const hasAddon = useCallback((addonKey) => {
    return entitlements?.addons?.[addonKey] === true;
  }, [entitlements]);

  // Update the current user's preferred UI language (persists server-side).
  const updateLocale = useCallback(async (locale) => {
    const response = await api.patch('/auth/me/locale', { locale });
    const newLocale = response.data?.preferred_locale || locale;
    setUser((prev) => {
      // Manual locale pick clears the one-time onboarding hint on the client too.
      const next = prev ? { ...prev, preferred_locale: newLocale, locale_onboarding_pending: false } : prev;
      if (next) localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
    try { await i18n.changeLanguage(newLocale); } catch (_) {}
    try { localStorage.setItem('sl_locale', newLocale); } catch (_) {}
    return newLocale;
  }, []);

  // Dismiss the one-time auto-detect onboarding toast (server + client).
  const dismissLocaleOnboarding = useCallback(async () => {
    try { await api.post('/auth/me/locale-onboarding-dismiss'); } catch (_) {}
    setUser((prev) => {
      const next = prev ? { ...prev, locale_onboarding_pending: false } : prev;
      if (next) localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const value = {
    user,
    permissions,
    entitlements,
    loading,
    login,
    signup,
    logout,
    setAuth,
    updateTenantTimezone,
    updateLocale,
    dismissLocaleOnboarding,
    locale: user?.preferred_locale || i18n.language || 'en',
    isAuthenticated: !!user,
    isOwner: user?.role === 'owner',
    isOffice: user?.role === 'office',
    isTech: user?.role === 'tech',
    assignedVanId: user?.assigned_van_id,
    hasPerm,
    hasEntitlement,
    hasAddon,
    refreshPermissions: fetchPermissions,
    refreshEntitlements: fetchEntitlements,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
